import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { AuthDataService } from '../auth/auth-data.service';
import { timer, Subscription } from 'rxjs';
import { APP_ROUTES } from '../../utils/route.constant';
import { DataService } from '../data/data.service';

@Injectable({
  providedIn: 'root'
})
export class IdleTrackerService {
  private idleTimeoutMs = 900 * 1000;  // 15 minutes in milliseconds
  private idleSubscription: Subscription | null = null;
  private activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart'];
  private activityHandler = () => this.resetTimer();

  constructor(
    private router: Router,
    private authData: AuthDataService,
    private ngZone: NgZone,
    private data: DataService
  ) {}

  startTracking(): void {
    this.stopTracking();
    this.ngZone.runOutsideAngular(() => {
      this.activityEvents.forEach(event =>
        window.addEventListener(event, this.activityHandler, true)
      );
    });
    this.resetTimer();
  }

  stopTracking(): void {
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
      this.idleSubscription = null;
    }
    this.activityEvents.forEach(event =>
      window.removeEventListener(event, this.activityHandler, true)
    );
  }

  private resetTimer = () => {
    if (this.idleSubscription) {
      this.idleSubscription.unsubscribe();
      this.idleSubscription = null;
    }
    // Create timer inside Angular zone to ensure it fires correctly
    this.ngZone.run(() => {
      this.idleSubscription = timer(this.idleTimeoutMs).subscribe({
        next: () => {
          this.ngZone.run(() => {
            if (
              [`/${APP_ROUTES.SESSION_TIMEOUT}`, `/${APP_ROUTES.LOGIN}`, `/${APP_ROUTES.OOPS_PAGE}`].includes(this.router.url)
            ) {
              // User is already on the session timeout page or login page or error page
              return;
            }
            // VIVR - If user is not logged in (neither Auth0 nor VIVR), redirect to session timeout page
            const isVivr = this.data.isVivr$.value;
            const hasVivrToken = !!this.data.vivrAccessTokenData$.value;
            const hasUser = !!this.data.user$.value;
            
            // User is not authenticated via Auth0 and in VIVR flow
            // idle for some time
            if (!hasUser && (isVivr || hasVivrToken)) {
              this.router.navigate([`/${APP_ROUTES.SESSION_TIMEOUT}`]);
              return;
            }
            // For logged-in users (Auth0 or VIVR), log them out on idle timeout
            this.authData.logout(true);
          });
        },
        error: (err) => {
          console.error('Timer subscription error:', err);
        }
      });
    });
  };

  ngOnDestroy(): void {
    this.stopTracking();
  }
}
